import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, UserCheck, Users } from "lucide-react";

interface TeamMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
}

interface ReassignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  currentAgentId: string | null;
  onReassigned?: () => void;
}

export default function ReassignDialog({
  open,
  onOpenChange,
  conversationId,
  currentAgentId,
  onReassigned,
}: ReassignDialogProps) {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedMember, setSelectedMember] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (open) {
      fetchTeamMembers();
    }
  }, [open]);

  const fetchTeamMembers = async () => {
    setFetching(true);
    
    // Fetch all agents and admins
    const { data: rolesData, error: rolesError } = await supabase
      .from("user_roles")
      .select("user_id, role")
      .in("role", ["agent", "admin"]);

    if (rolesError) {
      console.error("Error fetching roles:", rolesError);
      setFetching(false);
      return;
    }

    if (!rolesData || rolesData.length === 0) {
      setTeamMembers([]);
      setFetching(false);
      return;
    }

    // Fetch profiles for these users
    const userIds = rolesData.map((r) => r.user_id);
    const { data: profilesData } = await supabase
      .from("profiles")
      .select("id, first_name, last_name")
      .in("id", userIds);

    const profilesMap = new Map(profilesData?.map((p) => [p.id, p]) || []);

    const members: TeamMember[] = rolesData
      .filter((r) => r.user_id !== currentAgentId) // Exclude current assignee
      .map((r) => {
        const profile = profilesMap.get(r.user_id);
        return {
          id: r.user_id,
          first_name: profile?.first_name || null,
          last_name: profile?.last_name || null,
          role: r.role,
        };
      });

    setTeamMembers(members);
    setFetching(false);
  };

  const handleReassign = async () => {
    if (!selectedMember) {
      toast.error("Veuillez sélectionner un membre");
      return;
    }

    setLoading(true);

    const { error } = await supabase
      .from("conversations")
      .update({
        assigned_agent_id: selectedMember,
        updated_at: new Date().toISOString(),
      })
      .eq("id", conversationId);

    if (error) {
      toast.error("Échec du transfert");
      setLoading(false);
      return;
    }

    toast.success("Conversation transférée");
    setLoading(false);
    setSelectedMember("");
    onOpenChange(false);
    onReassigned?.();
  };

  const getDisplayName = (member: TeamMember) => {
    const name = `${member.first_name || ""} ${member.last_name || ""}`.trim();
    const roleLabel = member.role === "admin" ? "Admin" : "Concierge";
    return name ? `${name} (${roleLabel})` : `Utilisateur (${roleLabel})`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif">
            <Users size={20} />
            Transférer la conversation
          </DialogTitle>
          <DialogDescription>
            Sélectionnez un membre de l'équipe pour lui assigner cette conversation.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {fetching ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : teamMembers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Aucun autre membre disponible
            </p>
          ) : (
            <Select value={selectedMember} onValueChange={setSelectedMember}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir un membre..." />
              </SelectTrigger>
              <SelectContent>
                {teamMembers.map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    <div className="flex items-center gap-2">
                      <UserCheck size={14} className="text-muted-foreground" />
                      {getDisplayName(member)}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleReassign}
            disabled={!selectedMember || loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Confirmer le transfert"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
